import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { SubmitHandler, SubmitErrorHandler, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { boolean, number, object, SchemaOf, string, date } from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { DataGrid, GridRowId } from "@mui/x-data-grid";
import { useAuth } from "../../_auth/useAuth";
import {
    Form, FormSectionHeader, FormTitle,
    ButtonBar, DaButton,
    DaHiddenField, DaTextField, DaNumberFormatField,
    DaDateTimePickerField,
    IMessagePanelProps, ProgressPanel,
    NumberFormatType, HTMLComment,
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings, DaTabPanel,
    MuiGridDataProp, ChildGridToolbar,
    IDialogTextProps, DialogBox,
    MessageBox, MsgBoxType, IAutocompleteDialogTextProps,
} from "../_shared";
import { FormPaper, AevTabs, AevTab } from "./User.styles";
import { CustomerAddDialog, GroupAddDialog, BrokerAddDialog, selectedGroupNameStore } from "../_shared/dialog";
import {
    IItemIdRequest,
    IUser,
    IUserItemIdRequest,
    IUserItemIdsRequest,
    ILookupUserSearchTerm,
    userApi,
    customerApi,
    brokerApi
} from "../../_api";
import { lookupApi } from "../../_api/lookup.api";
import {
    AuthRolesEnum,
    userAEVFields,
    userAuthorizedCustomerColumns,
    userAuthorizedGroupColumns,
    userAuthorizedListAdminColumns
} from "../../_appData";
import { UserGroup } from "../../_appData/enums/userGroup";

/**
 * Extracts the email domain from an email.
 * Takes the word after @ and before the first dot.
 * Example:
 *   user@company.com -> returns 'company'
 *   user@noriina.shop -> returns 'noriina'
 */
const getEmailDomain = (email: string): string => {
    try {
      const domainPart = email.split('@')[1];
      return domainPart.replace(/\.[^.]+$/, '');
    } catch {
      return '';
    }
  }

const userSchema: SchemaOf<IUser> = object({
    id: number().integer().optional(),
    user_sid: string().max(50).optional(),
    first_name: string().label(userAEVFields.firstName.label).max(50).required(),
    last_name: string().label(userAEVFields.lastName.label).max(50).required(),
    email: string().label(userAEVFields.email.label).max(80).required(),
    phone: string().label(userAEVFields.phone.label).max(20).required(),
    is_active: boolean().label(userAEVFields.isActive.label).required(),
    create_datetime: date().optional(),
    modify_datetime: date().optional(),
    modify_user_name: string().optional(),
});

const userTypeListItems = [
    { value: "1", text: "Broker/Agent" },
    { value: "2", text: "List Administrator" },
    { value: "3", text: "Cooperative Database" },
]

export const UserAEView: React.FC = () => {
    const theme = useTheme();
    const { user, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const navigationState = location.state as { mode?: 'view' | 'edit' } | null;
    const isViewMode = navigationState?.mode === 'view';
    const isEditMode = navigationState?.mode === 'edit';
    const [customerSelectionModel, setCustomerSelectionModel] = useState<GridRowId[]>([]);
    const [brokerSelectionModel, setBrokerSelectionModel] = useState<GridRowId[]>([]);
    const [groupSelectionModel, setGroupSelectionModel] = useState<GridRowId[]>([]);

    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({ message: "" });
    const [progressTitle, setProgressTitle] = useState<string>("Getting User Info...");
    const [showError, setShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(true);
    const [selTabIndex, setSelTabIndex] = useState<number>(0);
    const [isCustomersLoading, setIsCustomersLoading] = useState<boolean>(true);
    const [isBrokersLoading, setIsBrokersLoading] = useState<boolean>(true);
    const [isGroupsLoading, setIsGroupsLoading] = useState<boolean>(true);
    const [userGroup, setUserGroup] = useState<string | null>(null); // Store user's group from API response
    const [dlgDelCustomersOpen, setDlgDelCustomersOpen] = React.useState<boolean>(false);
    const [dlgDelCustomersProps, setDlgDelCustomersProps] = useState<IDialogTextProps>({ message: "" });
    const [dlgAddCustomerOpen, setDlgAddCustomerOpen] = React.useState<boolean>(false);
    const [dlgAddCustomerProps, setDlgAddCustomerProps] = useState<IAutocompleteDialogTextProps>({ title: "Choose Authorized Customer" });
    const [dlgDelBrokersOpen, setDlgDelBrokersOpen] = React.useState<boolean>(false);
    const [dlgDelBrokersProps, setDlgDelBrokersProps] = useState<IDialogTextProps>({ message: "" });
    const [dlgAddListAdminOpen, setDlgAddListAdminOpen] = React.useState<boolean>(false);
    const [dlgAddListAdminProps, setDlgAddListAdminProps] = useState<IAutocompleteDialogTextProps>({ title: "Choose Authorized ListAdmin" });
    const [dlgDelGroupsOpen, setDlgDelGroupsOpen] = React.useState<boolean>(false);
    const [dlgDelGroupsProps, setDlgDelGroupsProps] = useState<IDialogTextProps>({ message: "" });
    const [dlgAddGroupOpen, setDlgAddGroupOpen] = React.useState<boolean>(false);
    const [dlgAddGroupProps, setDlgAddGroupProps] = useState<IAutocompleteDialogTextProps>({ title: "Choose Authorization Group" });
    const [selectedGroupName, setSelectedGroupName] = React.useState<string | undefined>(undefined);
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });

    // const [pageSize, setPageSize] = React.useState<number>(DaGridSettings.defaultPageSize);
    const [customerGridData, setCustomerGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });
    const [listAdminGridData, setListAdminGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });

    const [groupGridData, setGroupGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });

    const formFieldSize = "medium";
    const formFieldVariant = "standard";
    let userId: number | undefined = undefined;

    let aevMode: "add" | "upd" | "err" | undefined = undefined;

    const hasTabAccess = userHasPrivilege([AuthRolesEnum.Licensee]);

    // In view/edit mode: Check user's group from API response
    // In add mode: Check groups from grid data
    const isEditOrViewMode = (aevMode === "upd" || isViewMode);
    const hasLicenseeGroup = isEditOrViewMode
        ? (userGroup === UserGroup.LICENSEE)
        : groupGridData.rows.some((row: any) => row.group_name === UserGroup.LICENSEE);

    const hasBrokerGroup = isEditOrViewMode
        ? (userGroup === UserGroup.LIST_ADMIN)
        : groupGridData.rows.some((row: any) => row.group_name === UserGroup.LIST_ADMIN);

    const hasListOwnerGroup = isEditOrViewMode
        ? (userGroup === UserGroup.LIST_OWNER)
        : groupGridData.rows.some((row: any) => row.group_name === UserGroup.LIST_OWNER);

    // Broker and Company tabs appear only after at least one group is added from the Groups tab
    const hasGroups = groupGridData.rows.length > 0;

    // Show Company tab only when a group is added AND it is List Owner (no Licensee, no Broker)
    const showCompanyTab = hasTabAccess && hasGroups && !hasLicenseeGroup && !hasBrokerGroup;

    // Show Broker tab only when a group is added AND it is not Licensee (List Admin or List Owner)
    const showBrokerTab = hasTabAccess && hasGroups && !hasLicenseeGroup;

    // Calculate dynamic tab indices based on visible tabs
    const tabIndices = useMemo(() => {
        const indices: { user: number; groups?: number; company?: number; broker?: number } = { user: 0 };
        let currentIndex = 0;

        // Tab 0: User (always visible)
        indices.user = currentIndex++;

        // Tab 1: Groups (when hasTabAccess - so user can add a group)
        if (hasTabAccess) {
            indices.groups = currentIndex++;
        }

        // Tab 2: Company (only after group added and group type is List Owner)
        if (showCompanyTab) {
            indices.company = currentIndex++;
        }

        // Tab 3: Broker (only after group added and group is not Licensee)
        if (showBrokerTab) {
            indices.broker = currentIndex++;
        }

        return indices;
    }, [hasTabAccess, showCompanyTab, showBrokerTab]);

    // Calculate the last tab index dynamically
    const lastTabIndex = useMemo(() => {
        const values = Object.values(tabIndices);
        return values.length > 0 ? Math.max(...values) : 0;
    }, [tabIndices]);

    // List Owner has only one tab (User); Licensee may have multiple tabs
    const isSingleTab = lastTabIndex === 0;
    // Validate URL param
    if (params.id) {
        if (Number.isNaN(Number(params.id))) {
            if (params.id !== "new") {
                aevMode = "err";
            } else {
                userId = -1;
                aevMode = "add";
            }
        } else {
            userId = parseInt(params.id, 10);
            aevMode = "upd";
        }
    }

    // defining the initial state for the form
    const initialValues = {
        id: -1,
        user_sid: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        is_active: true,
        create_datetime: new Date(),
        modify_datetime: new Date(),
        modify_user_name: `${user.account_id}`,
    };

    const { control, handleSubmit, reset, trigger, watch, formState: { errors, isValid } } = useForm<IUser>({
        defaultValues: initialValues,
        resolver: yupResolver(userSchema),
        mode: 'onChange', // Validate on change to clear errors immediately
        reValidateMode: 'onChange' // Re-validate on change
    });

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const dlgAddCustomer_Show = (title?: string) => {
        setDlgAddCustomerProps({
            title: title,
        })
        setDlgAddCustomerOpen(true);
    }

    const dlgAddCustomer_Close = (value?: string, item_id?: number) => {
        setDlgAddCustomerOpen(false);

        if (value && value === "ok" && item_id) {
            // Call API to get company data (same way as CompanySelection)
            getCustomerDataById(item_id);
        }
    }

    // Get company data by ID and add to grid (same pattern as CompanySelection.getUserCompanyData)
    const getCustomerDataById = async (customerId: number): Promise<void> => {
        setIsCustomersLoading(true);
        const options: IItemIdRequest = { id: customerId as number };
        const auth_token = await getAccessToken();

        customerApi.get_by_id(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            const customer = resp.data;

                            // Map the API response to the grid row format based on userAuthorizedCustomerColumns
                            const newCustomerRow = {
                                company_id: customer.company_id || customer.customer_id,
                                customer_id: customer.customer_id || customer.company_id,
                                company_sid: customer.company_sid || customer.customer_sid || '',
                                company_name: customer.company_name,
                                dba_name: customer.dba_name || '',
                                representative_name: customer.representative_name || '',
                                representative_title: customer.representative_title || '',
                                representative_email: customer.representative_email || '',
                                modify_datetime: customer.modify_datetime || new Date(),
                            };

                            // In add mode: replace existing customer (only one at a time)
                            // In update mode: add if doesn't exist (allow multiple)
                            if (aevMode === "add") {
                                setCustomerGridData({
                                    rows: [newCustomerRow],
                                    row_count: 1,
                                });
                            } else {
                                // Update mode: check if customer already exists and add if it doesn't
                                setCustomerGridData(prev => {
                                    const existingIndex = prev.rows.findIndex(
                                        (row: any) => (row.company_id === newCustomerRow.company_id) || (row.customer_id === newCustomerRow.customer_id)
                                    );

                                    if (existingIndex === -1) {
                                        // Add to grid data only if it doesn't already exist
                                        return {
                                            rows: [...prev.rows, newCustomerRow],
                                            row_count: prev.row_count + 1,
                                        };
                                    }
                                    return prev;
                                });
                            }
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving Authorized Customer data. " +
                            "The Authorized Customer data was not found.",
                            "Authorized Customer Not Found Error",
                            "error"
                        );
                    }
                }
            })
            .catch(() => {
                msgBox_Show(
                    "An error occurred while fetching Authorized Customer data. " +
                    "There may have been a network error.",
                    "User API Fetch Error",
                    "error"
                );
            })
            .finally(() => {
                setIsCustomersLoading(false);
            });
    };

    const dlgDelCustomers_Close = (value?: string) => {
        const selRows = customerSelectionModel;
        setDlgDelCustomersOpen(false);

        if (value && value === "ok") {
            // For new user creation, just remove from local state (no API call)
            if (aevMode === "add") {
                setCustomerGridData(prev => {
                    const newRows = prev.rows.filter((row: any) =>
                        !selRows.includes(row.company_id) && !selRows.includes(row.customer_id)
                    );
                    return {
                        rows: newRows,
                        row_count: newRows.length,
                    };
                });
                setCustomerSelectionModel([]);
            } else {
                // For existing user, call API to delete
                deleteSelectedCustomers(selRows as (string | number)[]);
            }
        }
    }

    const dlgDelCustomers_Show = (message: string, title?: string) => {
        setDlgDelCustomersProps({
            message: message,
            title: title,
        })
        setDlgDelCustomersOpen(true);
    }

    const dlgAddListAdmin_Show = (title?: string) => {
        setDlgAddListAdminProps({
            title: title,
        })
        setDlgAddListAdminOpen(true);
    }

    const dlgAddListAdmin_Close = (value?: string, item_id?: number) => {
        setDlgAddListAdminOpen(false);

        if (value && value === "ok" && item_id) {
            // Call API to get broker data (same way as CustomerAddDialog)
            getBrokerDataById(item_id);
        }
    }

    // Get broker data by ID and add to grid (same pattern as getCustomerDataById)
    const getBrokerDataById = async (listadminId: number): Promise<void> => {
        setIsBrokersLoading(true);
        const options: IItemIdRequest = { id: listadminId as number };
        const auth_token = await getAccessToken();

        brokerApi.get_by_id(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            const broker = resp.data;
                            const company = broker.company || {};
                            const user = broker.user || {};

                            // Map the customer/company API response to the grid row format based on userAuthorizedListAdminColumns
                            // The API returns company data, but we need to map it to broker grid format
                            const newBrokerRow = {
                                listadmin_id: broker.listadmin_id,
                                listadmin_sid: broker.listadmin_sid,
                                company_id: company.company_id || '',
                                company_sid: company.company_sid || '',
                                listadmin_type: broker.listadmin_type,
                                company_name: company.company_name || '',
                                phone: company.phone || '',
                                naics_code: company.naics_code || '',
                                state: company.state || '',
                                postal_code: company.postal_code || '',
                                email: user.email || '',
                                
                            };

                            // In add mode: replace existing broker (only one at a time)
                            // In update mode: add if doesn't exist (allow multiple)
                            if (aevMode === "add") {
                                setListAdminGridData({
                                    rows: [newBrokerRow],
                                    row_count: 1,
                                });
                            } else {
                                // Update mode: check if broker already exists and add if it doesn't
                                setListAdminGridData(prev => {
                                    const existingIndex = prev.rows.findIndex(
                                        (row: any) => row.listadmin_id === broker.listadmin_id
                                    );

                                    if (existingIndex === -1) {
                                        // Add to grid data only if it doesn't already exist
                                        return {
                                            rows: [...prev.rows, newBrokerRow],
                                            row_count: prev.row_count + 1,
                                        };
                                    }
                                    return prev;
                                });
                            }
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving Authorized Broker data. " +
                            "The Authorized Broker data was not found.",
                            "Authorized Broker Not Found Error",
                            "error"
                        );
                    }
                }
            })
            .catch(() => {
                msgBox_Show(
                    "An error occurred while fetching Authorized Broker data. " +
                    "There may have been a network error.",
                    "User API Fetch Error",
                    "error"
                );
            })
            .finally(() => {
                setIsBrokersLoading(false);
            });
    };

    // Helper function to add/replace a group in the grid
    const addGroupToGrid = (groupName: string): void => {
        // In add mode: replace existing group (only one at a time)
        // In update mode: add if doesn't exist (allow multiple)
        if (aevMode === "add") {
            setGroupGridData({
                rows: [{ group_name: groupName }],
                row_count: 1,
            });
        } else {
            // Update mode: check if group already exists and add if it doesn't
            setGroupGridData(prev => {
                const existingIndex = prev.rows.findIndex(
                    (row: any) => row.group_name === groupName
                );

                if (existingIndex === -1) {
                    return {
                        rows: [...prev.rows, { group_name: groupName }],
                        row_count: prev.row_count + 1,
                    };
                }
                return prev;
            });
        }
    };

    // Helper function to extract groups array from API response
    const extractGroupsFromResponse = (data: any): Array<any> => {
        if (Array.isArray(data)) {
            return data;
        }
        if (data && Array.isArray(data.authorized_groups)) {
            return data.authorized_groups;
        }
        if (data && typeof data === 'object') {
            const dataKeys = Object.keys(data);
            const arrayKey = dataKeys.find(key => Array.isArray(data[key]));
            if (arrayKey) {
                return data[arrayKey];
            }
        }
        return [];
    };

    // Add group to grid by name (for new user mode)
    const getGroupDataByName = async (groupName: string): Promise<void> => {
        setIsGroupsLoading(true);

        try {
            const options: ILookupUserSearchTerm = { user_id: userId || -1, search_term: '' };
            const auth_token = await getAccessToken();
            const resp = await lookupApi.get_user_group_lookup(options, auth_token);

            if (resp && resp.success) {
                const groups = extractGroupsFromResponse(resp.data);
                // Verify the group exists in the API response (optional validation)
                const groupExists = groups.some((g: any) => g.group_name === groupName);
                if (!groupExists) {
                    // Group not in API response, but still add it (API might be incomplete)
                }
            }
            // Always add the group to grid (whether found in API or not)
            addGroupToGrid(groupName);
        } catch (err) {
            // On error, still add the group to grid
            addGroupToGrid(groupName);
        } finally {
            setIsGroupsLoading(false);
        }
    };

    const dlgDelBrokers_Close = (value?: string) => {
        const selRows = brokerSelectionModel;
        setDlgDelBrokersOpen(false);

        if (value && value === "ok") {
            // For new user creation, just remove from local state (no API call)
            if (aevMode === "add") {
                setListAdminGridData(prev => {
                    const newRows = prev.rows.filter((row: any) =>
                        !selRows.includes(row.listadmin_id)
                    );
                    return {
                        rows: newRows,
                        row_count: newRows.length,
                    };
                });
                setBrokerSelectionModel([]);
            } else {
                // For existing user, call API to delete
                deleteSelectedListAdmins(selRows as (string | number)[]);
            }
        }
    }

    const dlgDelBrokers_Show = (message: string, title?: string) => {
        setDlgDelBrokersProps({
            message: message,
            title: title,
        })
        setDlgDelBrokersOpen(true);
    }

    const dlgAddGroup_Show = (title?: string) => {
        setDlgAddGroupProps({
            title: title,
        })
        setDlgAddGroupOpen(true);
    }

    const dlgAddGroup_Close = async (value?: string, item_id?: number) => {
        setDlgAddGroupOpen(false);

        if (value && value === "ok") {
            // Get the selected group_name from the shared store
            const selectedGroupName = selectedGroupNameStore.groupName;

            if (selectedGroupName) {
                // For new user creation, add group directly using group_name
                if (aevMode === "add") {
                    getGroupDataByName(selectedGroupName);
                } else if (userId && userId > 0 && item_id) {
                    // For existing user, call API to add group
                    const options: IUserItemIdRequest = {
                        user_id: userId as number,
                        item_id: item_id as number,
                    };
                    addGroupAuth(options);
                }
                // Clear the store after use
                selectedGroupNameStore.groupName = undefined;
            } else {
                msgBox_Show(
                    "Please select a group from the dropdown.",
                    "No Group Selected",
                    "warning"
                );
            }
        }
    }

    const dlgDelGroups_Close = (value?: string) => {
        const selRows = groupSelectionModel;
        setDlgDelGroupsOpen(false);

        if (value && value === "ok") {
            // For new user creation, just remove from local state (no API call)
            if (aevMode === "add") {
                setGroupGridData(prev => {
                    const newRows = prev.rows.filter((row: any) =>
                        !selRows.includes(row.group_name)
                    );
                    return {
                        rows: newRows,
                        row_count: newRows.length,
                    };
                });
                setGroupSelectionModel([]);
            } else {
                // For existing user, call API to delete
                deleteSelectedGroups(selRows as (string | number)[]);
            }
        }
    }

    const dlgDelGroups_Show = (message: string, title?: string) => {
        setDlgDelGroupsProps({
            message: message,
            title: title,
        })
        setDlgDelGroupsOpen(true);
    }

    const onSubmit: SubmitHandler<IUser> = (data) => {
        saveUserData(data);
    };

    const onSubmitErrors: SubmitErrorHandler<IUser> = (errors) => {
        const messages: string[] = [];

        const walk = (obj: any) => {
            Object.values(obj).forEach((val: any) => {
                if (val?.message) messages.push(val.message);
                if (val && typeof val === "object") walk(val);
            });
        };

        walk(errors);

        msgBox_Show(
            `Please fix the following required fields:\n\n${messages
                .map((m) => `• ${m}`)
                .join("\n")}`,
            "Invalid User Info",
            "error"
        );
    };

    const showErrorPanel = ({ title, message, type = "error" }: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setShowError(true);
    }

    const handleTabChange = (e: React.SyntheticEvent, idx: number) => {
        setSelTabIndex(idx);
    }

    const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (isViewMode) {
            // In view mode, just navigate to next tab without validation
            if (selTabIndex < lastTabIndex) {
                setSelTabIndex(selTabIndex + 1);
            }
        } else {
            // In edit/add mode, validate and navigate
            handleNextTab();
        }
    }

    const handlePrevious = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (selTabIndex > 0) {
            setSelTabIndex(selTabIndex - 1);
        }
    }

    const handleNextTab = async () => {
        if (selTabIndex < lastTabIndex) {
            let isValid = true;

            // Validate fields based on current tab
            if (selTabIndex === 0) {
                // Validate User tab fields (first_name, last_name, email)
                isValid = await trigger(['first_name', 'last_name', 'email']);
            }
            // Tab 1 (Customers) and Tab 2 (ListAdmins) don't have required fields to validate

            // Only move to next tab if validation passes
            // Validation errors will be shown in the UI automatically by react-hook-form
            if (isValid) {
                setSelTabIndex(selTabIndex + 1);
            }
        }
    }

    const handlePreviousTab = () => {
        if (selTabIndex > 0) {
            setSelTabIndex(selTabIndex - 1);
        }
    }

    const saveUserData = async (options: any): Promise<void> => {
        // Extract group_names first to determine group type
        const groupNames = groupGridData.rows.map((row: any) => row.group_name).filter((name: any) => name !== undefined && name !== null && name !== '');

        // Check if Licensee or Broker group is present
        const hasLicenseeGroup = groupNames.includes(UserGroup.LICENSEE);
        const hasBrokerGroup = groupNames.includes(UserGroup.LIST_ADMIN);
        const hasListOwnerGroup = groupNames.includes(UserGroup.LIST_OWNER);

        // Validate user email domain matches company representative_email domain
        // Only validate when a company is selected (i.e., NOT Licensee and NOT Broker group)
        if (!hasLicenseeGroup && !hasBrokerGroup && customerGridData.rows.length > 0) {
            const userEmail = options.email || '';
            const userEmailDomain = getEmailDomain(userEmail);

            // Check all selected companies for domain match
            for (const company of customerGridData.rows) {
                const companyRepEmail = company.representative_email || '';
                const companyEmailDomain = getEmailDomain(companyRepEmail);

                if (userEmailDomain && companyEmailDomain && userEmailDomain !== companyEmailDomain) {
                    msgBox_Show(
                        "User email does not match with the selected company domain. Please check.",
                        "Email Domain Mismatch",
                        "error"
                    );
                    return; // Stop the save operation
                }
            }
        }

        // Validate user email domain matches company representative_email domain
        // Only validate when a company is selected (i.e., NOT Licensee and NOT Broker group)
        if (!hasLicenseeGroup && !hasListOwnerGroup && listAdminGridData.rows.length > 0) {
            const userEmail = options.email || '';
            const userEmailDomain = getEmailDomain(userEmail);
            // Check all selected companies for domain match
            for (const company of listAdminGridData.rows) {

                const companyRepEmail = company.email || '';
                const companyEmailDomain = getEmailDomain(companyRepEmail);
                if (userEmailDomain && companyEmailDomain && userEmailDomain !== companyEmailDomain) {
                    msgBox_Show(
                        "User email does not match with the selected company domain. Please check.",
                        "Email Domain Mismatch",
                        "error"
                    );
                    return; // Stop the save operation
                }
            }
        }
        setProgressTitle("Saving User Info...");
        setShowProgress(true);

        const auth_token = await getAccessToken();

        // Include company and listadmin IDs if they were added
        let payload = { ...options };

        // Extract company_ids and listadmin_ids from grid data
        const companyIds = customerGridData.rows.map((row: any) => row.company_id).filter((id: any) => id !== undefined);
        const listAdminIds = listAdminGridData.rows.map((row: any) => row.listadmin_id).filter((id: any) => id !== undefined);

        // Payload logic based on selected groups:
        // - If Licensee group: Don't send authorized_companies AND authorized_list_admins
        // - If Broker group: Don't send authorized_companies (but can send authorized_list_admins)
        // - Otherwise: Send both if they have data (previous behavior)

        if (!hasLicenseeGroup) {
            // Include authorized_companies only if Broker group is NOT present
            if (!hasBrokerGroup && companyIds.length > 0) {
                payload.authorized_companies = companyIds;
            }

            // Include authorized_list_admins if there are items
            // (This applies to all cases except when Licensee group is present)
            if (listAdminIds.length > 0) {
                payload.authorized_list_admins = listAdminIds;
            }
        }
        // If hasLicenseeGroup is true, we don't include authorized_companies or authorized_list_admins

        // Include authorized_groups as array of group name strings
        if (groupNames.length > 0) {
            payload.authorized_groups = groupNames;
        }

        let apiPromise = undefined;
        if (aevMode === "add") {
            apiPromise = userApi.add(payload, auth_token);
        } else if (aevMode === "upd") {
            apiPromise = userApi.update(payload, auth_token);
        } else {
            showErrorPanel({
                title: "Inconsistent Form State Error",
                message: "An error occurred while saving User data. " +
                    "Navigate to the User list view and retry your last action."
            });
            setShowProgress(false);
        }

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                navigate("/users");
            } else {
                msgBox_Show(
                    "An error occurred while saving User data. Verify that you have provided all required data and retry your last action.",
                    "Unable to Save User",
                    "error"
                );
            }
        })
            .catch(() => {
                msgBox_Show(
                    "An error occurred while saving User data. There may have been a network error.",
                    "User API Save Error",
                    "error"
                );
            })
            .finally(() => {
                setShowProgress(false);
            });
    };

    const getUserCustomerData = async (): Promise<void> => {
        setIsCustomersLoading(true);

        const options: IItemIdRequest = { id: userId as number };
        const auth_token = await getAccessToken();

        userApi.customers_get(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            // Ensure each row has company_id (primary) and customer_id (for compatibility)
                            const rows = (resp.data || []).map((row: any) => ({
                                ...row,
                                company_id: row.company_id || row.customer_id || row.id,
                                customer_id: row.customer_id || row.company_id || row.id
                            }));
                            setCustomerGridData({
                                rows,
                                row_count: resp.count!,
                            });
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        showErrorPanel({
                            title: "Authorized Customers Not Found Error",
                            message: "An error occurred while retrieving Authorized Customers data. " +
                                "The Authorized Customers data was not found.",
                        });
                    }
                }
            })
            .catch(() => {
                showErrorPanel({
                    title: "User API Fetch Error",
                    message: "An error occurred while fetching Authorized Customers data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setIsCustomersLoading(false);
            });
    };

    const getUserListAdminData = async (): Promise<void> => {
        setIsBrokersLoading(true);

        const options: IItemIdRequest = { id: userId as number };
        const auth_token = await getAccessToken();

        userApi.listadmins_get(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            setListAdminGridData({
                                rows: resp.data,
                                row_count: resp.count!,
                            });
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        showErrorPanel({
                            title: "Authorized ListAdmins Not Found Error",
                            message: "An error occurred while retrieving Authorized ListAdmins data. " +
                                "The Authorized Customers data was not found.",
                        });
                    }
                }
            })
            .catch(() => {
                showErrorPanel({
                    title: "User API Fetch Error",
                    message: "An error occurred while fetching Authorized ListAdmins data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setIsBrokersLoading(false);
            });
    };

    const getUserGroupData = async (): Promise<void> => {
        setIsGroupsLoading(true);

        const options: IItemIdRequest = { id: userId as number };
        const auth_token = await getAccessToken();

        userApi.groups_get(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            setGroupGridData({
                                rows: resp.data,
                                row_count: resp.count!,
                            });
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        showErrorPanel({
                            title: "Authorization Groups Not Found Error",
                            message: "An error occurred while retrieving Authorization Groups data. " +
                                "The Authorization Groups data was not found.",
                        });
                    }
                }
            })
            .catch(() => {
                showErrorPanel({
                    title: "User API Fetch Error",
                    message: "An error occurred while fetching Authorization Groups data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setIsGroupsLoading(false);
            });
    };

    const getUserData = useCallback(async (): Promise<void> => {
        setProgressTitle("Getting User Data...");
        setShowProgress(true);

        if (aevMode === "err") {
            showErrorPanel({
                title: "Invalid User ID",
                message: "An error occurred while retrieving User data. " +
                    "Please go to the User list view and retry your latest action."
            });
            setShowProgress(false);
        } else if (aevMode === "add" && !isViewMode) {
            // New User - no data to load, set all loading states to false
            setIsCustomersLoading(false);
            setIsBrokersLoading(false);
            setIsGroupsLoading(false);
            setUserGroup(null); // Reset user group in add mode
            setShowProgress(false);
        } else if ((aevMode === "upd" || isViewMode) && userId && userId > 0) {
            const options: IItemIdRequest = { id: userId as number };
            const auth_token = await getAccessToken();

            userApi.get_by_id(options, auth_token)
                .then((resp) => {
                    if (resp) {
                        if (resp.success) {
                            // Map authorized_groups - already in correct format
                            if (resp.data.authorized_groups) {
                                setGroupGridData({
                                    rows: resp.data.authorized_groups,
                                    row_count: resp.data.authorized_groups.length,
                                });
                            }

                            // Map authorized_companies (not authorized_customers)
                            if (resp.data.authorized_companies) {
                                const rows = (resp.data.authorized_companies || []).map((row: any) => ({
                                    company_id: row.company_id,
                                    customer_id: row.company_id, // For compatibility
                                    company_sid: row.company_sid || '',
                                    company_name: row.company_name || '',
                                    dba_name: row.dba_name || '',
                                    representative_name: row.representative_name || '',
                                    representative_title: row.representative_title || '',
                                    representative_email: row.representative_email || '',
                                    modify_datetime: row.modify_datetime || new Date(),
                                }));
                                setCustomerGridData({
                                    rows,
                                    row_count: rows.length,
                                });
                            }

                            // Map authorized_list_admins - flatten nested company data
                            if (resp.data.authorized_list_admins) {
                                const rows = (resp.data.authorized_list_admins || []).map((row: any) => {
                                    const company = row.company || {};
                                    return {
                                        listadmin_id: row.listadmin_id,
                                        listadmin_sid: row.listadmin_sid || '',
                                        company_id: company.company_id || '',
                                        company_sid: company.company_sid || '',
                                        listadmin_type: row.listadmin_type || 1,
                                        company_name: company.company_name || '',
                                        phone: company.phone || '',
                                        naics_code: company.naics_code || '',
                                        state: company.state || '',
                                        postal_code: company.postal_code || '',
                                        modify_datetime: row.modify_datetime || company.modify_datetime || new Date(),
                                    };
                                });
                                setListAdminGridData({
                                    rows,
                                    row_count: rows.length,
                                });
                            }
                            // Store user's group from API response for tab visibility in view/edit mode
                            if (resp.data.group) {
                                setUserGroup(resp.data.group);
                            }
                            reset(resp.data as IUser);
                        } else if (resp.error && resp.error.length > 0) {
                            showErrorPanel({
                                title: "User Not Found Error",
                                message: "An error occurred while retrieving User data. " +
                                    "The User data was not found.",
                            });
                        }
                    }
                })
                .catch(() => {
                    showErrorPanel({
                        title: "User API Fetch Error",
                        message: "An error occurred while fetching User data. " +
                            "There may have been a network error.",
                    });
                })
                .finally(() => {
                    setIsCustomersLoading(false);
                    setIsGroupsLoading(false);
                    setIsBrokersLoading(false);
                    setShowProgress(false);
                });
        } else {
            // If we reach here and we're in view mode but conditions weren't met, show error
            if (isViewMode) {
                showErrorPanel({
                    title: "Invalid User ID",
                    message: "An error occurred while retrieving User data. " +
                        "Please go to the User list view and retry your latest action."
                });
            }
            setShowProgress(false);
            setIsCustomersLoading(false);
            setIsGroupsLoading(false);
            setIsBrokersLoading(false);
        }

    }, [reset, userId, aevMode, isViewMode]);

    useEffect(() => {
        // Get data from remote service
        const resp = getUserData();
    }, [getUserData]);

    const listAdminsGridToolbar = () => {
        // In view mode, disable both Add and Delete buttons
        // In edit mode, if user's group is Broker, disable Select and Delete in Broker section
        const brokerSectionDisabled = isViewMode || (isEditMode && hasBrokerGroup);
        return (
            <ChildGridToolbar
                showEditButton={false}
                showResetButton={false}
                onClickAdd={onClickListAdminAdd}
                onClickDelete={onClickListAdminDelete}
                disabledAdd={brokerSectionDisabled}
                disabledDelete={brokerSectionDisabled}
                addButtonText="Select"
            />
        );
    }

    const onClickListAdminAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
        // Display File Upload dialog
        dlgAddListAdmin_Show("Choose Authorized Broker");
    };

    const onClickListAdminDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgDelBrokers_Close for processing of dialog result
        dlgDelBrokers_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const addListAdminAuth = async (options: IUserItemIdRequest): Promise<void> => {
        setIsBrokersLoading(true);

        const auth_token = await getAccessToken();
        let apiPromise = userApi.listadmins_add(options, auth_token);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                // Refresh all user data using get_by_id instead of separate API calls
                getUserData();
            } else {
                msgBox_Show(
                    "An error occurred while adding Authorized ListAdmins... " +
                    "please retry you last action. If you continue to experience issues, " +
                    "contact customer support.",
                    "Unable to Add Authorized ListAdmins...",
                    "warning"
                );
            }
        })
            .catch(() => {
                msgBox_Show(
                    "An error occurred while adding Authorized ListAdmins data. " +
                    "There may have been a network error.",
                    "User API Error",
                    "warning"
                );
            })
            .finally(() => {
                setIsBrokersLoading(false);
            });
    };

    const deleteSelectedListAdmins = async (row_ids: Array<string | number>) => {
        setIsBrokersLoading(true);

        const options: IUserItemIdsRequest = { user_id: userId!, item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        userApi.listadmins_remove(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        // Refresh all user data using get_by_id instead of separate API calls
                        getUserData();
                        msgBox_Show(
                            `${resp.count} Authorized ListAdmin(s) have been deleted...`,
                            "Delete Authorized ListAdmins",
                            "info"
                        );
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while deleting Authorized ListAdmins... " +
                            "please retry you last action. If you continue to experience issues, " +
                            "contact customer support.",
                            "Unable to Delete Authorized ListAdmins...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsBrokersLoading(false);
            });
    };

    const customersGridToolbar = () => {
        // In view mode, disable both Add and Delete buttons
        // Otherwise, allow adding/replacing (only one item at a time)
        const customersSectionDisabled = isViewMode || (isEditMode && hasListOwnerGroup);
        return (
            <ChildGridToolbar
                showEditButton={false}
                showResetButton={false}
                onClickAdd={onClickCustomerAdd}
                onClickDelete={onClickCustomerDelete}
                disabledAdd={customersSectionDisabled}
                disabledDelete={customersSectionDisabled}
                addButtonText="Select"
            />
        );
    }

    const onClickCustomerAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
        // Display Add Customer dialog
        dlgAddCustomer_Show("Choose Authorized Customer");
    };

    const onClickCustomerDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgDelCustomers_Close for processing of dialog result
        dlgDelCustomers_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const deleteSelectedCustomers = async (row_ids: Array<string | number>) => {
        setIsCustomersLoading(true);

        const options: IUserItemIdsRequest = { user_id: userId!, item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        userApi.customers_remove(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        // Refresh all user data using get_by_id instead of separate API calls
                        getUserData();
                        msgBox_Show(
                            `${resp.count} Authorized Customer(s) have been deleted...`,
                            "Delete Authorized Customers",
                            "info"
                        );
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while deleting Authorized Customers... " +
                            "please retry you last action. If you continue to experience issues, " +
                            "contact customer support.",
                            "Unable to Delete Authorized Customers...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsCustomersLoading(false);
            });
    };

    const groupsGridToolbar = () => {
        // In view mode, disable both Add and Delete buttons
        // In edit mode, if user's group is List Owner, disable Select and Delete in Groups section
        const groupsSectionDisabled = isViewMode || isEditMode;
        return (
            <ChildGridToolbar
                showEditButton={false}
                showResetButton={false}
                onClickAdd={onClickGroupAdd}
                onClickDelete={onClickGroupDelete}
                disabledAdd={groupsSectionDisabled}
                disabledDelete={groupsSectionDisabled}
            />
        );
    }

    const onClickGroupAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
        // Display Add Customer dialog
        dlgAddGroup_Show("Choose Authorization Group");
    };

    const onClickGroupDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgDelCustomers_Close for processing of dialog result
        dlgDelGroups_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const addGroupAuth = async (options: any): Promise<void> => {
        setIsGroupsLoading(true);

        const auth_token = await getAccessToken();
        let apiPromise = userApi.groups_add(options, auth_token);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                // Refresh all user data using get_by_id instead of separate API calls
                getUserData();
            } else {
                msgBox_Show(
                    "An error occurred while adding Authorization Groups... " +
                    "please retry you last action. If you continue to experience issues, " +
                    "contact customer support.",
                    "Unable to Add Authorization Groups...",
                    "warning"
                );
            }
        })
            .catch(() => {
                msgBox_Show(
                    "An error occurred while adding Authorization Groups data. " +
                    "There may have been a network error.",
                    "User API Error",
                    "warning"
                );
            })
            .finally(() => {
                setIsGroupsLoading(false);
            });
    };

    const deleteSelectedGroups = async (row_ids: Array<string | number>) => {
        setIsGroupsLoading(true);

        const options: IUserItemIdsRequest = { user_id: userId!, item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        userApi.groups_remove(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        // Refresh all user data using get_by_id instead of separate API calls
                        getUserData();
                        msgBox_Show(
                            `${resp.count} Authorization Group(s) have been deleted...`,
                            "Delete Authorization Groups",
                            "info"
                        );
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while deleting Authorization Groups... " +
                            "please retry you last action. If you continue to experience issues, " +
                            "contact customer support.",
                            "Unable to Delete Authorization Groups...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsGroupsLoading(false);
            });
    };

    return (
        <>
            {userId &&
                <>
                    <FormPaper elevation={4}>
                        {/* Form Title*/}
                        <FormTitle
                            title={
                                aevMode === "add"
                                    ? "User: New "
                                    : isViewMode
                                        ? "User: View"
                                        : "User: Update"
                            }
                            titleIcon={"person_rounded_icon"}
                        />

                        <Grid container>
                            {showProgress ? (
                                <Grid container justifyContent={"center"} item>
                                    <ProgressPanel title={progressTitle} />
                                </Grid>
                            ) : (
                                <Grid item xs={12}>
                                    <Form
                                        autoComplete="off"
                                        onSubmit={handleSubmit(onSubmit, onSubmitErrors)}
                                    >
                                        <AevTabs value={selTabIndex} onChange={(_, i) => setSelTabIndex(i)}>
                                            <AevTab label="User" />
                                            {hasTabAccess && <AevTab label="User Type" />}
                                            {showCompanyTab && <AevTab label="Company" />}
                                            {showBrokerTab && <AevTab label="Broker" />}

                                        </AevTabs>
                                        <Box sx={{ minHeight: theme.spacing(60) }}>
                                            <DaTabPanel selectedIndex={selTabIndex} index={0}>
                                                <Grid container spacing={theme.spacing(1)}>
                                                    {/* List Admin Section Header */}
                                                    <Grid item xs={12} md={12}>
                                                        <FormSectionHeader
                                                            title={"User Info"}
                                                            typeVariant={"h5"}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <DaTextField
                                                            control={control}
                                                            name={userAEVFields.userSid.name}
                                                            label={userAEVFields.userSid.label}
                                                            readOnly
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            errors={errors}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DaTextField
                                                            control={control}
                                                            name={userAEVFields.firstName.name}
                                                            label={userAEVFields.firstName.label}
                                                            required={userAEVFields.firstName.required}
                                                            readOnly={isViewMode}
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            errors={errors}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DaTextField
                                                            control={control}
                                                            name={userAEVFields.lastName.name}
                                                            label={userAEVFields.lastName.label}
                                                            required={userAEVFields.lastName.required}
                                                            readOnly={isViewMode}
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            errors={errors}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DaTextField
                                                            control={control}
                                                            name={userAEVFields.email.name}
                                                            label={userAEVFields.email.label}
                                                            required={userAEVFields.email.required}
                                                            readOnly={isViewMode || aevMode === "upd"}
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            errors={errors}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DaNumberFormatField
                                                            control={control}
                                                            name={userAEVFields.phone.name}
                                                            label={userAEVFields.phone.label}
                                                            required={userAEVFields.phone.required}
                                                            readOnly={isViewMode}
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            errors={errors}
                                                            numberFormat={NumberFormatType.US_Phone}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <DaDateTimePickerField
                                                            control={control}
                                                            name={userAEVFields.modifyDatetime.name}
                                                            label={userAEVFields.modifyDatetime.label}
                                                            readOnly
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            disableOpenPicker={true}
                                                            errors={errors}
                                                        />
                                                    </Grid>
                                                    {/* <Grid item xs={12} md={4}>
                                                        <DaTextField
                                                            control={control}
                                                            name={userAEVFields.modifyUserName.name}
                                                            label={userAEVFields.modifyUserName.label}
                                                            readOnly
                                                            variant={formFieldVariant}
                                                            size={formFieldSize}
                                                            errors={errors}
                                                        />
                                                    </Grid> */}
                                                    <Grid item xs={12} md={4}>
                                                        <HTMLComment text={"Placeholder"} />
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <DaHiddenField
                                                            control={control}
                                                            name={userAEVFields.createDatetime.name}
                                                            errors={errors}
                                                        />
                                                        <DaHiddenField
                                                            control={control}
                                                            name={userAEVFields.userId.name}
                                                            errors={errors}
                                                        />
                                                        <DaHiddenField
                                                            control={control}
                                                            name={userAEVFields.isActive.name}
                                                            errors={errors}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </DaTabPanel>
                                            {showCompanyTab && tabIndices.company !== undefined && (
                                                <DaTabPanel selectedIndex={selTabIndex} index={tabIndices.company}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Authorized Companies"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaGridContainer
                                                                sx={{
                                                                    height: 430,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <DataGrid
                                                                    components={{
                                                                        LoadingOverlay: DaGridLoadingOverlay,
                                                                        Toolbar: customersGridToolbar,
                                                                    }}
                                                                    initialState={{
                                                                        pagination: {
                                                                            page: 0,
                                                                            pageSize: DaGridSettings.defaultPageSize,
                                                                        },
                                                                    }}
                                                                    loading={isCustomersLoading}
                                                                    columns={userAuthorizedCustomerColumns}
                                                                    rows={customerGridData.rows}
                                                                    rowCount={customerGridData.row_count}
                                                                    getRowId={(row) => row.company_id}
                                                                    rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                                                    headerHeight={DaGridSettings.headerRowHeight}
                                                                    rowHeight={DaGridSettings.rowHeight}
                                                                    checkboxSelection
                                                                    disableSelectionOnClick
                                                                    selectionModel={customerSelectionModel}
                                                                    onSelectionModelChange={(newSelection) => {
                                                                        setCustomerSelectionModel(newSelection);
                                                                    }}
                                                                />
                                                            </DaGridContainer>
                                                        </Grid>
                                                    </Grid>
                                                </DaTabPanel>
                                            )}
                                            {showBrokerTab && tabIndices.broker !== undefined && (
                                                <DaTabPanel selectedIndex={selTabIndex} index={tabIndices.broker}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Authorized Brokers"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaGridContainer
                                                                sx={{
                                                                    height: 430,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <DataGrid
                                                                    components={{
                                                                        LoadingOverlay: DaGridLoadingOverlay,
                                                                        Toolbar: listAdminsGridToolbar,
                                                                    }}
                                                                    initialState={{
                                                                        pagination: {
                                                                            page: 0,
                                                                            pageSize: DaGridSettings.defaultPageSize,
                                                                        },
                                                                    }}
                                                                    loading={isBrokersLoading}
                                                                    columns={userAuthorizedListAdminColumns}
                                                                    rows={listAdminGridData.rows}
                                                                    rowCount={listAdminGridData.row_count}
                                                                    getRowId={(row) => row.listadmin_id}
                                                                    rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                                                    headerHeight={DaGridSettings.headerRowHeight}
                                                                    rowHeight={DaGridSettings.rowHeight}
                                                                    checkboxSelection={!(isViewMode || (aevMode === "upd" && hasBrokerGroup))}
                                                                    disableSelectionOnClick
                                                                    selectionModel={brokerSelectionModel}
                                                                    onSelectionModelChange={(newSelection) => {
                                                                        setBrokerSelectionModel(newSelection);
                                                                    }}
                                                                />
                                                            </DaGridContainer>
                                                        </Grid>
                                                    </Grid>
                                                </DaTabPanel>
                                            )}
                                            {hasTabAccess && tabIndices.groups !== undefined && (
                                                <DaTabPanel selectedIndex={selTabIndex} index={tabIndices.groups}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Authorization Group"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaGridContainer
                                                                sx={{
                                                                    height: 430,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <DataGrid
                                                                    components={{
                                                                        LoadingOverlay: DaGridLoadingOverlay,
                                                                        Toolbar: groupsGridToolbar,
                                                                    }}
                                                                    initialState={{
                                                                        pagination: {
                                                                            page: 0,
                                                                            pageSize: DaGridSettings.defaultPageSize,
                                                                        },
                                                                    }}
                                                                    loading={isGroupsLoading}
                                                                    columns={userAuthorizedGroupColumns}
                                                                    rows={groupGridData.rows}
                                                                    rowCount={groupGridData.row_count}
                                                                    getRowId={(row) => row.group_name}
                                                                    rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                                                    headerHeight={DaGridSettings.headerRowHeight}
                                                                    rowHeight={DaGridSettings.rowHeight}
                                                                    checkboxSelection={!(isViewMode || (aevMode === "upd" && hasListOwnerGroup))}
                                                                    disableSelectionOnClick
                                                                    selectionModel={groupSelectionModel}
                                                                    onSelectionModelChange={(newSelection) => {
                                                                        setGroupSelectionModel(newSelection);
                                                                    }}
                                                                />
                                                            </DaGridContainer>
                                                        </Grid>
                                                    </Grid>
                                                </DaTabPanel>
                                            )}
                                        </Box>
                                        <Grid container spacing={theme.spacing(1)}>
                                            <Grid item xs={12} md={12}>
                                                <ButtonBar buttonAlignment="alignRight">
                                                    {isViewMode ? (
                                                        // View mode: buttons based on tab count and current tab
                                                        isSingleTab ? (
                                                            // List Owner / single tab: Cancel only (no Next)
                                                            <DaButton buttonStyle="secondary" onClick={() => navigate("/users")} type="button">
                                                                Cancel
                                                            </DaButton>
                                                        ) : (
                                                            // Multi-tab: First = Cancel + Next; Middle = Prev + Next + Cancel; Last = Prev + Cancel
                                                            <>
                                                                {selTabIndex > 0 && (
                                                                    <DaButton buttonStyle="secondary" type="button" onClick={(e) => handlePrevious(e)}>
                                                                        Previous
                                                                    </DaButton>
                                                                )}
                                                                {selTabIndex < lastTabIndex && (
                                                                    <DaButton buttonStyle="primary" type="button" onClick={(e) => handleNext(e)}>
                                                                        Next
                                                                    </DaButton>
                                                                )}
                                                                <DaButton buttonStyle="secondary" onClick={() => navigate("/users")} type="button">
                                                                    Cancel
                                                                </DaButton>
                                                            </>
                                                        )
                                                    ) : (
                                                        // Edit/Add mode: buttons based on tab count and current tab
                                                        isSingleTab ? (
                                                            // List Owner / single tab: Cancel + Save only (no Next/Prev)
                                                            <>
                                                                <DaButton buttonStyle="secondary" onClick={() => navigate("/users")} type="button">
                                                                    Cancel
                                                                </DaButton>
                                                                {userHasPrivilege([AuthRolesEnum.ListOwner, AuthRolesEnum.Licensee]) && (
                                                                    <DaButton buttonStyle="primary" type="submit">
                                                                        Save User
                                                                    </DaButton>
                                                                )}
                                                            </>
                                                        ) : (
                                                            // Multi-tab (Licensee): Cancel, Prev/Next by tab, Save on last tab
                                                            <>
                                                                <DaButton buttonStyle="secondary" onClick={() => navigate("/users")} type="button">
                                                                    Cancel
                                                                </DaButton>
                                                                {selTabIndex > 0 && (
                                                                    <DaButton buttonStyle="secondary" onClick={handlePreviousTab} type="button">
                                                                        Previous
                                                                    </DaButton>
                                                                )}
                                                                {selTabIndex < lastTabIndex && (
                                                                    <DaButton buttonStyle="secondary" onClick={handleNextTab} type="button">
                                                                        Next
                                                                    </DaButton>
                                                                )}
                                                                {selTabIndex === lastTabIndex && (
                                                                    <DaButton buttonStyle="primary" type="submit">
                                                                        Save User
                                                                    </DaButton>
                                                                )}
                                                            </>
                                                        )
                                                    )}
                                                </ButtonBar>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                </Grid>
                            )}
                        </Grid>
                    </FormPaper>
                </>
            }
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgDelCustomersOpen}
                onClose={dlgDelCustomers_Close}
                {...dlgDelCustomersProps}
            />
            <CustomerAddDialog
                isOpen={dlgAddCustomerOpen}
                onClose={dlgAddCustomer_Close}
                userId={userId}
                userEmail={watch("email")}
                {...dlgAddCustomerProps}
            />
            <DialogBox
                isOpen={dlgDelGroupsOpen}
                onClose={dlgDelGroups_Close}
                {...dlgDelGroupsProps}
            />
            <GroupAddDialog
                isOpen={dlgAddGroupOpen}
                onClose={(value, item_id) => {
                    // Get the selected group_name from the dialog's internal state
                    // We need to modify the dialog to expose this, or use a ref
                    dlgAddGroup_Close(value, item_id);
                }}
                userId={userId}
                {...dlgAddGroupProps}
            />
            <DialogBox
                isOpen={dlgDelBrokersOpen}
                onClose={dlgDelBrokers_Close}
                {...dlgDelBrokersProps}
            />
            <BrokerAddDialog
                isOpen={dlgAddListAdminOpen}
                onClose={dlgAddListAdmin_Close}
                userId={userId}
                {...dlgAddListAdminProps}
            />
        </>
    );
}